// Dispatch desk session on the account (DISPATCH_DESK_SESSION_PLAN.md, 10.3).
//
// Thin wrappers over the packer endpoints plus the save queue for
// PUT /dispatch/session/pack-state. Every call resolves to a CallResult —
// nothing throws — so the desk can treat "server sessions off" (network,
// 5xx, 404 on an old BE, 503 desk_session_unavailable, 403) as one case and
// fall back to its sessionStorage mirror.
import { apiFetch } from "../../api/apiFetch";
import type { DeskClaim, DeskPackState, DispatchOrder } from "../../types/Dispatch";

export type ServerSession = {
    warehouse: string;
    desk_id: string | null;
    unit: unknown | null;
    view: string | null;
    order: DispatchOrder | null;
    order_opened_at: string | null;
    pack_state: DeskPackState | null;
    version: number | null;
    released?: { reason: string; status?: string };
};

export type SessionPatch = {
    desk_id?: string | null;
    warehouse?: string;
    unit?: unknown | null;
    view?: string;
};

export type OpenOrderData = {
    order_id?: string;
    pack_state?: DeskPackState | null;
    version?: number | null;
};

export type PackingSession = {
    uid: number;
    user_name: string | null;
    desk_id: string | null;
    order_id: string;
    order_number: string | null;
    order_opened_at: string | null;
    updated_at: string | null;
    units_done: number;
    units_total: number;
    boxes: number;
};

type Body = Record<string, any>;

export type CallResult<T> =
    | { ok: true; data: T }
    // network / 5xx (transient) or 404 / 503 / 403 (permanent: this BE has no sessions for us)
    | { ok: false; kind: "unavailable"; permanent: boolean }
    | { ok: false; kind: "unauthorized" }
    | { ok: false; kind: "conflict"; code: string | null; body: Body }
    | { ok: false; kind: "rejected"; status: number; body: Body };

const call = async <T>(
    method: string,
    path: string,
    payload?: unknown,
    opts?: { notFoundIsRejection?: boolean },
): Promise<CallResult<T>> => {
    let res: Response;
    try {
        res = await apiFetch(path, {
            method,
            ...(payload !== undefined
                ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
                : {}),
        });
    } catch {
        return { ok: false, kind: "unavailable", permanent: false };
    }
    let parsed: unknown = null;
    try { parsed = await res.json(); } catch { /* no body */ }
    const body: Body = parsed && typeof parsed === "object" ? (parsed as Body) : {};
    if (res.ok) return { ok: true, data: body as T };
    if (res.status === 401) return { ok: false, kind: "unauthorized" };
    if (res.status === 409) {
        return { ok: false, kind: "conflict", code: typeof body.code === "string" ? body.code : null, body };
    }
    if (res.status === 503 || res.status === 403) return { ok: false, kind: "unavailable", permanent: true };
    if (res.status >= 500) return { ok: false, kind: "unavailable", permanent: false };
    if (res.status === 404) {
        // open-order answers 404 + { message } for a missing order; any other
        // 404 is an old BE without the route.
        if (opts?.notFoundIsRejection && typeof body.message === "string") {
            return { ok: false, kind: "rejected", status: 404, body };
        }
        return { ok: false, kind: "unavailable", permanent: true };
    }
    return { ok: false, kind: "rejected", status: res.status, body };
};

// ── Packer endpoints ───────────────────────────────────────────────

export const getSession = () => call<{ session?: ServerSession | null }>("GET", "/dispatch/session");

export const putSession = (patch: SessionPatch) =>
    call<{ session?: ServerSession | null }>("PUT", "/dispatch/session", patch);

export const openOrder = (orderId: string, packState?: DeskPackState | null) =>
    call<OpenOrderData>(
        "POST",
        "/dispatch/session/open-order",
        { order_id: orderId, ...(packState ? { pack_state: packState } : {}) },
        { notFoundIsRejection: true },
    );

export type SaveBody = { order_id: string; pack_state: DeskPackState; view: string; version: number };

export const putPackState = (body: SaveBody) =>
    call<{ version?: number }>("PUT", "/dispatch/session/pack-state", body);

export const releaseOrder = (orderId: string) =>
    call<{ released?: boolean }>("POST", "/dispatch/session/release-order", { order_id: orderId });

// ── Leader endpoints (admin.warehouse.outbound) ───────────────────

export const listPackingSessions = (warehouse: string) =>
    call<{ sessions?: PackingSession[] }>("GET", `/dispatch/sessions?warehouse=${encodeURIComponent(warehouse)}`);

export const getOrderClaim = (orderId: string) =>
    call<{ claim?: DeskClaim | null }>("GET", `/dispatch/orders/${encodeURIComponent(orderId)}/claim`);

export const leaderReleaseOrder = (orderId: string) =>
    call<{ released?: boolean; claim?: DeskClaim }>("POST", `/dispatch/orders/${encodeURIComponent(orderId)}/release`, {});

// ── Save queue ─────────────────────────────────────────────────────

export type PackStateSaverOptions = {
    /** Injected for tests; defaults to putPackState. */
    send?: (body: SaveBody) => Promise<CallResult<{ version?: number }>>;
    debounceMs?: number;
    retryMs?: number;
    /** Another tab moved on: apply the server's state (version already adopted). */
    onVersionConflict?: (e: { orderId: string; packState: DeskPackState | null; version: number }) => void;
    /** A leader released the order: the queue has stopped. */
    onClaimLost?: (e: { orderId: string; claim: DeskClaim | null }) => void;
    /** The server answers 404 / 503 / 403: sessions are off, the queue dropped its state. */
    onUnavailable?: () => void;
};

export type PackStateSaver = {
    /** Bind the queue to an order. version = the last one the server told us (null = none, sent as 0). */
    begin: (orderId: string, version: number | null) => void;
    /** Queue the latest state; debounced, coalesced. No-op when no order is bound. */
    schedule: (packState: DeskPackState, view: string) => void;
    /** Send now and resolve once nothing is pending or in flight (or a retry is parked). */
    flush: () => Promise<void>;
    /** Drop pending state and unbind; results of requests still in flight are ignored. */
    cancel: () => void;
    getVersion: () => number;
    setVersion: (version: number) => void;
    hasPending: () => boolean;
    isBound: () => boolean;
};

/**
 * One request in flight, the latest state coalesced behind it, 800 ms
 * debounce on the way in. A request that dies on the network keeps its
 * state and is retried; a 409 version_conflict adopts the server's version
 * and hands its state to the caller; a 409 claim_lost stops the queue.
 */
export const createPackStateSaver = (opts: PackStateSaverOptions = {}): PackStateSaver => {
    const send = opts.send ?? putPackState;
    const debounceMs = opts.debounceMs ?? 800;
    const retryMs = opts.retryMs ?? 5000;

    let orderId: string | null = null;
    let version = 0;
    let gen = 0;
    let pending: { packState: DeskPackState; view: string } | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pump: Promise<void> | null = null;

    const clearTimer = () => { if (timer) { clearTimeout(timer); timer = null; } };
    const startTimer = (ms: number) => {
        clearTimer();
        timer = setTimeout(() => { timer = null; void startPump(); }, ms);
    };

    const run = async () => {
        while (pending && orderId) {
            const job = pending;
            pending = null;
            const myGen = gen;
            const oid = orderId;
            let res: CallResult<{ version?: number }>;
            try {
                res = await send({ order_id: oid, pack_state: job.packState, view: job.view, version });
            } catch {
                res = { ok: false, kind: "unavailable", permanent: false };
            }
            if (myGen !== gen) continue; // order changed / cancelled while in flight

            if (res.ok) {
                if (typeof res.data?.version === "number") version = res.data.version;
                continue;
            }
            if (res.kind === "conflict") {
                if (res.code === "version_conflict") {
                    pending = null;
                    if (typeof res.body.version === "number") version = res.body.version;
                    opts.onVersionConflict?.({
                        orderId: oid,
                        packState: (res.body.pack_state as DeskPackState | null) ?? null,
                        version,
                    });
                } else {
                    // claim_lost, or any other refusal that means "this order is not yours to save"
                    gen += 1;
                    clearTimer();
                    pending = null;
                    orderId = null;
                    opts.onClaimLost?.({ orderId: oid, claim: (res.body.claim as DeskClaim | null) ?? null });
                }
                continue;
            }
            if (res.kind === "unavailable") {
                if (res.permanent) {
                    pending = null;
                    opts.onUnavailable?.();
                    break;
                }
                if (!pending) pending = job; // keep it for the retry
                startTimer(retryMs);
                break;
            }
            // unauthorized (the global handler logs out) / rejected: nothing to retry
            pending = null;
        }
    };

    const startPump = (): Promise<void> => {
        if (!pump) pump = run().finally(() => { pump = null; });
        return pump;
    };

    return {
        begin(id, ver) {
            gen += 1;
            clearTimer();
            pending = null;
            orderId = id;
            version = ver ?? 0;
        },
        schedule(packState, view) {
            if (!orderId) return;
            pending = { packState, view };
            startTimer(debounceMs);
        },
        async flush() {
            clearTimer();
            if (pending && orderId) void startPump();
            if (pump) await pump;
        },
        cancel() {
            gen += 1;
            clearTimer();
            pending = null;
            orderId = null;
        },
        getVersion: () => version,
        setVersion(v) { version = v; },
        hasPending: () => pending !== null,
        isBound: () => orderId !== null,
    };
};
